// Styles
import "./accounts.scss";
// Tools
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useMemo } from "react";
// Components
import { AccountsHeader } from "./components/accounts-header/AccountsHeader";
import { AccountRow } from "./components/account-row/AccountRow";
import { AccountDetailsModal } from "./components/account-details-modal/AccountDetailsModal";
import { CompactSelect } from "../../../../../../../../shared/components/selects/compact-select/CompactSelect";
import { CircleLoader } from "../../../../../../../../shared/components/circle-loader/CircleLoader";
import { EmptyList } from "../../../../../../../../shared/components/empty-list/EmptyList";
// Utils
import { hideModuleThunk } from "../../../../utils/module-utils/hide-module";
import {
	buildSelectStatuses,
	AccountSelectItem,
} from "../../../../utils/component-utils/build-select-statuses";
import { processAccounts } from "./utils/processAccounts";
import { sumCostColumnUsd } from "../../../../../../../../shared/utils/sum-cost-column-usd";
import { normalizeStatusKey } from "../../../../../../../../shared/utils/normalize-status-label";
import {
	setSortParameters,
	getSortParameters,
} from "./utils/setSortParameters";
import {
	setFilterParameters,
	getFilterParameters,
} from "./utils/setFilterParameters";
import {
	getAccountCertDisplay,
	groupCertificatesByAccount,
	normalizeAccountId,
} from "./utils/account-certificates";
import { resolveCertPresence } from "./utils/cert-presence";
import type { AccountCertState } from "../../../../../../../../shared/types/certificates.type";
// Service
import { useDashboardData } from "../../../../../../service/dashboard-data.service";
import { useCertificates } from "../../../../../../service/certificates.service";
import type { AdAccount } from "../../../../../../../../shared/types/dashboard-data.type";
import { RootState } from "../../../../../../../../shared/store/store";

function indexAccountCertMeta(
	accounts: AccountCertState[] | undefined,
): Map<string, AccountCertState> {
	const map = new Map<string, AccountCertState>();
	for (const entry of accounts ?? []) {
		const key = normalizeAccountId(entry.account_id);
		if (key) map.set(key, entry);
	}
	return map;
}

export const Accounts = () => {
	const dispatch = useDispatch<any>();
	const { data, isAwaitingDashboardContent } = useDashboardData();
	const {
		data: certificatesData,
		isSuccess: certificatesReady,
		isError: certificatesFailed,
	} = useCertificates();
	const certsByAccount = useMemo(
		() => groupCertificatesByAccount(certificatesData?.certificates),
		[certificatesData?.certificates],
	);
	// DAI-1216: collection + last_scrape for every sub-account in one payload —
	// no per-row account/card fetches (those hit rate limits on the dashboard).
	const certMetaByAccount = useMemo(
		() => indexAccountCertMeta(certificatesData?.accounts),
		[certificatesData?.accounts],
	);
	const certMetaReady = certificatesReady || certificatesFailed;
	const highlightedAccounts = useSelector(
		(state: RootState) => state.app.highlightedAccounts,
	);
	const isFiltering = highlightedAccounts.length > 0;

	// ------ States
	// Элементы для выпадающего списка (фильтрация)
	const [accountSelectItems, setAccountSelectItems] = useState<
		AccountSelectItem[]
	>([]);
	const [selectedStatus, setSelectedStatus] = useState<string>(
		getFilterParameters() ?? "all",
	);
	// Критерий и способ сортировки
	const [sortInfo, setSortInfo] = useState({
		sortMethod: getSortParameters().sortMethod ?? "asc",
		sortBy: getSortParameters().sortBy ?? "status",
	});
	const [selectedAccount, setSelectedAccount] = useState<AdAccount | null>(
		null,
	);
	const accountImage = "/img/icons/large-64/account.png";

	// ------ Functions
	// Переключение метода и обьекта сортировки
	const handleSort = (method: string, by: string) => {
		setSortInfo({
			sortMethod: method,
			sortBy: by,
		});
		setSortParameters(method, by);
	};

	// ------ Effects
	useEffect(() => {
		const accountsData = data?.ad_accounts?.data;
		const forSelectStatuses = (accountsData ?? []).map((acc) => ({
			...acc,
			status:
				normalizeStatusKey(acc.status) === "enabled" ? "ACTIVE" : acc.status,
		}));
		buildSelectStatuses(forSelectStatuses, "status", setAccountSelectItems);
	}, [data]);

	useEffect(() => {
		if (selectedStatus === "enabled") {
			setSelectedStatus("active");
			return;
		}
		if (selectedStatus === "all") return;
		if (isAwaitingDashboardContent) return;
		if (accountSelectItems.length === 0) return;
		// Поки в списку лише «Всі статуси», дані ще не підвантажились — не скидаємо збережений фільтр
		if (
			accountSelectItems.length === 1 &&
			accountSelectItems[0]?.key === "all"
		) {
			return;
		}
		if (!accountSelectItems.some((i) => i.key === selectedStatus)) {
			setSelectedStatus("all");
			setFilterParameters("all");
		}
	}, [accountSelectItems, selectedStatus, isAwaitingDashboardContent]);

	const handleSelect = (key: string) => {
		setSelectedStatus(key);
		setFilterParameters(key);
	};

	const sortedAccounts = processAccounts(
		data?.ad_accounts.data,
		selectedStatus,
		sortInfo,
	);

	const accountsTotalCostUsd = sumCostColumnUsd(sortedAccounts);

	return (
		<div className='accounts window' data-app-highlight-boundary=''>
			<div className='window__header'>
				<div className='window__header_left'>
					<img src='/img/icons/accounts.png' alt='accounts icon' />
					<div>Облікові записи</div>
				</div>
				<div className='window__header_center'>
					<CompactSelect
						items={accountSelectItems}
						value={selectedStatus}
						onSelect={handleSelect}
					/>
				</div>
				<div className='window__header_right'>
					<div
						className='window__header_right_close-button'
						role='button'
						aria-label='hide accounts'
						onClick={() => dispatch(hideModuleThunk("accounts"))}>
						<img src='/img/icons/close.png' alt='close button' />
					</div>
				</div>
			</div>
			<div className='window__body'>
				<AccountsHeader
					setSortInfo={sortInfo}
					handleSort={handleSort}
					totalCostUsd={accountsTotalCostUsd}
				/>
				{isAwaitingDashboardContent ? (
					<div className='window__loader'>
						<CircleLoader />
					</div>
				) : (
					<div className='window__body_list'>
						<div className='window__body_list-inner'>
							{sortedAccounts.map((item: AdAccount) => {
								const accountKey = normalizeAccountId(item.account_id);
								const accountCerts = certsByAccount.get(accountKey) ?? [];
								const certDisplay = getAccountCertDisplay(accountCerts);
								const meta = certMetaByAccount.get(accountKey);
								const certPresence = resolveCertPresence({
									certificates: accountCerts,
									collection: meta?.collection,
									lastScrape: meta?.last_scrape,
									metaReady: certMetaReady,
								});
								return (
									<AccountRow
										key={item.account_id}
										title={item.account_name}
										id={item.account_id}
										status={item.status === "ENABLED" ? "ACTIVE" : item.status}
										cost={item.cost_usd}
										certsChips={certDisplay.visible}
										certsMore={certDisplay.more}
										certsMoreTooltip={certDisplay.moreTooltip}
										certPresence={certPresence}
										dimmed={
											isFiltering &&
											!highlightedAccounts.includes(item.account_id)
										}
										onOpen={() => setSelectedAccount(item)}
									/>
								);
							})}
						</div>
					</div>
				)}
				{!isAwaitingDashboardContent && sortedAccounts.length === 0 && (
					<div className='window__loader'>
						<EmptyList image={accountImage} text={"Облікові записи відсутні"} />
					</div>
				)}
			</div>
			<AccountDetailsModal
				isOpen={selectedAccount !== null}
				accountId={selectedAccount?.account_id ?? ""}
				fallbackAccountName={selectedAccount?.account_name}
				onClose={() => setSelectedAccount(null)}
			/>
		</div>
	);
};
